import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useHistory } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import { dateFormat } from 'constants/formats';

import Button from 'modules/_shared/components/Buttons';

import { TradingOfferType } from 'types/TradingOffer';
import { useAddTradingOfferPage } from '../context/AddTradingOfferPageContext';

const AddTradingOfferResumeSection: FC = () => {
  const { t } = useTranslate();
  const history = useHistory();
  const { data, actions } = useAddTradingOfferPage();

  const isSellOffer = data.tradingOfferType === TradingOfferType.SELL;

  const isValidateDates =
    data.startDate &&
    data.endDate &&
    new Date(data.startDate) < new Date(data.endDate);

  const isReviewDisabled =
    !data.sharesToTransfer || !data.pricePerShare || !isValidateDates;

  const offerDuration = useMemo(() => {
    if (!data.startDate || !data.endDate) return '-';
    const days = differenceInDays(
      new Date(data.endDate),
      new Date(data.startDate)
    );
    return t('NDays', { count: days });
  }, [data.startDate, data.endDate, t]);

  const summaryTitle = isSellOffer
    ? t('YourSellOfferSummary')
    : t('YourBuyOfferSummary');

  const typeBadgeClass = isSellOffer
    ? 'badge badge-dim badge-success'
    : 'badge badge-dim badge-primary';

  const typeLabel = isSellOffer ? t('SellOffer') : t('BuyOffer');

  const sharesAfterSale = data.partnerCurrentSharesCount
    ? data.partnerCurrentSharesCount - data.sharesToTransfer
    : 0;

  return (
    <div className="col-lg-5">
      <div
        className="card card-bordered sticky-top summary-sticky"
        style={{ top: '80px' }}
      >
        <div className="card-inner">
          <div className="text-center mb-3">
            <em className="icon ni ni-offer fs-24px text-info" />
            <h5 className="mt-2 mb-1">{summaryTitle}</h5>
            <p className="text-soft fs-13px mb-0">
              {t('ReviewDetailsBeforePublishing')}
            </p>
          </div>

          <hr />

          <div className="summary-line">
            <span className="text-soft">{t('Type')}</span>
            <strong>
              <span className={typeBadgeClass}>{typeLabel}</span>
            </strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('Class')}</span>
            <strong>{data.selectedHoldingClassOption?.name || '-'}</strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('SharesNumber')}</span>
            <strong>{formatNumber(data.sharesToTransfer)}</strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('PricePerShare')}</span>
            <strong>{formatCurrencyDecimals(data.pricePerShare)}</strong>
          </div>

          <div className="summary-line border-top pt-2">
            <span className="text-soft">{t('TotalPrice')}</span>
            <strong className="text-primary fs-18px">
              {formatCurrencyDecimals(data.totalOfferPrice)}
            </strong>
          </div>

          <hr />

          <div className="summary-line">
            <span className="text-soft">{t('StartDate')}</span>
            <strong>
              {data.startDate
                ? format(new Date(data.startDate), dateFormat)
                : '-'}
            </strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('EndDate')}</span>
            <strong>
              {data.endDate ? format(new Date(data.endDate), dateFormat) : '-'}
            </strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('Duration')}</span>
            <strong>{offerDuration}</strong>
          </div>

          {isSellOffer && (
            <div className="summary-line">
              <span className="text-soft">{t('PartialAcceptance')}</span>
              <strong>
                {data.isPartialAmountEnabled ? t('Yes') : t('Not')}
                {data.isPartialAmountEnabled &&
                  data.minSharesPerAcceptance > 0 && (
                    <span className="text-soft fs-13px ml-1">
                      (mín. {data.minSharesPerAcceptance})
                    </span>
                  )}
              </strong>
            </div>
          )}

          <hr />

          {isSellOffer && data.sharesToTransfer > 0 && (
            <div className="alert alert-light mb-3">
              <h6 className="alert-heading fs-13px">
                {t('AfterPublishingOffer')}:
              </h6>
              <ul className="list fs-13px mb-0">
                <li className="list-item pb-0">
                  {t('SharesWillBeReserved', {
                    count: formatNumber(data.sharesToTransfer),
                  })}
                </li>
                <li className="list-item pb-0">
                  {t('SharesRemainingAfterOffer', {
                    count: formatNumber(sharesAfterSale),
                  })}
                </li>
                <li className="list-item pb-0">
                  {t('YouCanCancelOfferAnytime')}
                </li>
              </ul>
            </div>
          )}

          <Button
            className="btn-block"
            onClick={actions.handleReviewTradingOffer}
            disabled={isReviewDisabled}
            lgSize
          >
            <em className="icon ni ni-send mr-2" />
            {t('ReviewOffer')}
          </Button>

          <div className="text-center mt-2">
            <button
              type="button"
              className="btn btn-link text-soft fs-13px"
              onClick={() =>
                history.push(`/mercado-de-acciones/${data.societyId}`)
              }
            >
              <em className="icon ni ni-arrow-left mr-1" />
              {t('Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTradingOfferResumeSection;
